import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Users, Home, TrendingUp, Star } from "lucide-react";

const stats = [
  { icon: Users, value: 3940, suffix: "+", label: "spokojených klientů", decimals: 0, gradient: "from-sky-400/20 to-blue-600/10", glow: "rgba(56,189,248,0.25)" },
  { icon: Home, value: 1670, suffix: "+", label: "prodaných nemovitostí", decimals: 0, gradient: "from-indigo-400/20 to-blue-500/10", glow: "rgba(129,140,248,0.25)" },
  { icon: TrendingUp, value: 6, suffix: " %", label: "průměrné navýšení hodnoty aukcí", decimals: 0, gradient: "from-emerald-400/20 to-teal-500/10", glow: "rgba(52,211,153,0.25)" },
  { icon: Star, value: 4.9, suffix: "★", label: "hodnocení Google", decimals: 1, gradient: "from-amber-400/20 to-yellow-500/10", glow: "rgba(251,191,36,0.25)" },
];

const CountUp = ({ end, decimals, duration = 2 }: { end: number; decimals: number; duration?: number }) => {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (!isInView) return;
    let start = 0;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * end;
      setCount(current);
      if (progress < 1) requestAnimationFrame(animate);
    };

    requestAnimationFrame(animate);
  }, [isInView, end, duration]);

  return <span ref={ref}>{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}</span>;
};

const StatsSection = () => {
  return (
    <section className="py-14 sm:py-20 overflow-hidden relative">
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(220,15%,12%)] to-[hsl(0,0%,3%)]" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.03] via-transparent to-transparent pointer-events-none" />
      <div className="container-luxury relative z-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="text-center group"
            >
              {/* Unique gradient icon with glow ring */}
              <motion.div
                whileHover={{ scale: 1.2, rotate: 8 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 400, damping: 15 }}
                className={`icon-glossy inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.gradient} border border-white/[0.08] mb-5 relative cursor-pointer backdrop-blur-sm`}
                style={{ boxShadow: `0 0 0 1px rgba(255,255,255,0.05), 0 8px 32px -8px ${stat.glow}` }}
              >
                <stat.icon className="h-7 w-7 text-white/90" strokeWidth={1.5} />
                {/* Animated glow on hover */}
                <motion.div
                  className="absolute -inset-1 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{ background: `radial-gradient(circle, ${stat.glow}, transparent 70%)`, filter: 'blur(12px)' }}
                />
              </motion.div>

              {/* Counting number */}
              <div className="text-3xl sm:text-4xl font-heading font-bold text-white mb-2">
                <CountUp end={stat.value} decimals={stat.decimals} />
                <motion.span
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.7 }}
                  viewport={{ once: true }}
                  transition={{ delay: 2 + i * 0.15, duration: 0.4 }}
                  className="text-white/60"
                >
                  {stat.suffix}
                </motion.span>
              </div>

              {/* Label with underline animation */}
              <div className="relative inline-block">
                <span className="text-xs sm:text-sm text-white/45 uppercase tracking-widest font-medium">
                  {stat.label}
                </span>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + i * 0.15, duration: 0.6, ease: "easeOut" }}
                  className="absolute -bottom-2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent origin-left"
                />
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
